import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Checkbox,
  Paper,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import { usePickupRequests } from "../hooks/usePickupRequests";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: es });
  } catch {
    return dateStr;
  }
};

// Solo fecha (YYYY-MM-DD), sin conversión de zona horaria
const formatDateOnly = (dateStr) => {
  if (!dateStr) return "—";
  const [y, m, d] = String(dateStr).slice(0, 10).split("-");
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
};

const STATUS_LABELS = {
  created_on_courier: "Creado en courier",
  fulfilled: "Despachado",
  withdrawn: "Retirado",
  in_transit: "En tránsito",
  in_cd: "En CD",
  dropped_at_hub: "En hub",
  on_the_way: "En camino",
};

function AssignToManifest() {
  const navigate = useNavigate();
  const {
    pickupRequests,
    eligibleOrders,
    availableCouriers,
    loading,
    error,
    setError,
    success,
    setSuccess,
    filters,
    setFilters,
    fetchEligibleOrders,
    assignOrders,
    requestManifest,
  } = usePickupRequests();

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPickupForAssign, setSelectedPickupForAssign] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(null);

  // Fetch eligible orders whenever any filter changes
  useEffect(() => {
    fetchEligibleOrders();
  }, [fetchEligibleOrders]);

  const handleCourierChange = (value) => {
    setFilters((prev) => ({ ...prev, courier: value }));
    setSelectedOrders([]);
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(eligibleOrders.map((o) => o.order_id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleAssignConfirm = async () => {
    if (!selectedPickupForAssign) return;
    try {
      const result = await assignOrders(selectedPickupForAssign, selectedOrders);
      setSelectedOrders([]);
      setAssignDialogOpen(false);
      setSelectedPickupForAssign(null);
      if (result?.manifest_url) {
        setSuccess(
          `Pedidos asignados. Manifiesto disponible.`
        );
      }
      await fetchEligibleOrders();
    } catch (_) {
      setAssignDialogOpen(false);
    }
  };

  const handleManifest = async (pickupId) => {
    setManifestLoading(pickupId);
    try {
      const result = await requestManifest(pickupId);
      if (result?.manifest_url) {
        window.open(result.manifest_url, "_blank");
      }
    } catch (_) {
    } finally {
      setManifestLoading(null);
    }
  };

  const pickupsForAssignDialog = pickupRequests.filter(
    (p) => !filters.courier || p.courier === filters.courier
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs>
            <Typography variant="h5" fontWeight="bold">
              Asignar a manifiesto
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Asocia pedidos a solicitudes de retiro y genera el manifiesto de entrega.
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/home/pickup-requests/create")}
            >
              Nueva solicitud de retiro
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Courier</InputLabel>
                <Select
                  value={filters.courier}
                  label="Courier"
                  onChange={(e) => handleCourierChange(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {availableCouriers.map(({ courier }) => (
                    <MenuItem key={courier} value={courier}>
                      {courier.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="Desde"
                value={filters.date_from ? new Date(filters.date_from) : null}
                onChange={(d) =>
                  setFilters((prev) => ({
                    ...prev,
                    date_from: d ? format(d, "yyyy-MM-dd") : "",
                  }))
                }
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="Hasta"
                value={filters.date_to ? new Date(filters.date_to) : null}
                onChange={(d) =>
                  setFilters((prev) => ({
                    ...prev,
                    date_to: d ? format(d, "yyyy-MM-dd") : "",
                  }))
                }
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Tabla superior: solicitudes de retiro */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Solicitudes de retiro
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Courier</strong></TableCell>
                  <TableCell><strong>ID Recogida</strong></TableCell>
                  <TableCell><strong>Fecha creación</strong></TableCell>
                  <TableCell><strong>Fecha programada</strong></TableCell>
                  <TableCell><strong>Pedidos asignados</strong></TableCell>
                  <TableCell><strong>Manifiesto</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pickupRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: "text.secondary" }}>
                      No hay solicitudes de retiro creadas.
                    </TableCell>
                  </TableRow>
                )}
                {pickupRequests.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.id}</TableCell>
                    <TableCell>
                      <Chip label={p.courier?.toUpperCase()} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{p.external_pickup_id}</TableCell>
                    <TableCell>{formatDate(p.created_at)}</TableCell>
                    <TableCell>{formatDateOnly(p.scheduled_date)}</TableCell>
                    <TableCell>{p.assigned_orders_count ?? "—"}</TableCell>
                    <TableCell>
                      {p.manifest_url ? (
                        <Tooltip title="Abrir manifiesto">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => window.open(p.manifest_url, "_blank")}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Solicitar manifiesto (fallback)">
                          <span>
                            <IconButton
                              size="small"
                              color="default"
                              disabled={manifestLoading === p.id}
                              onClick={() => handleManifest(p.id)}
                            >
                              {manifestLoading === p.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PrintIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Tabla inferior: pedidos elegibles */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Grid item xs>
            <Typography variant="h6">
              Pedidos elegibles
              {selectedOrders.length > 0 && (
                <Chip
                  label={`${selectedOrders.length} seleccionados`}
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Grid>
          {selectedOrders.length > 0 && (
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setAssignDialogOpen(true)}
              >
                Asignar a solicitud de retiro
              </Button>
            </Grid>
          )}
        </Grid>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedOrders.length > 0 &&
                      selectedOrders.length < eligibleOrders.length
                    }
                    checked={
                      eligibleOrders.length > 0 &&
                      selectedOrders.length === eligibleOrders.length
                    }
                    onChange={handleSelectAll}
                    disabled={eligibleOrders.length === 0}
                  />
                </TableCell>
                <TableCell><strong>Orden</strong></TableCell>
                <TableCell><strong>Referencia</strong></TableCell>
                <TableCell><strong>Fecha creación</strong></TableCell>
                <TableCell><strong>Courier</strong></TableCell>
                <TableCell><strong>Tracking</strong></TableCell>
                <TableCell><strong>Etiqueta</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Asignado</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eligibleOrders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: "text.secondary" }}>
                    No hay pedidos elegibles
                    {filters.courier ? ` para ${filters.courier.toUpperCase()}` : ""}.
                  </TableCell>
                </TableRow>
              )}
              {eligibleOrders.map((order) => (
                <TableRow
                  key={order.order_id}
                  hover
                  selected={selectedOrders.includes(order.order_id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedOrders.includes(order.order_id)}
                      onChange={() => handleSelectOrder(order.order_id)}
                      disabled={order.assigned_to_pickup}
                    />
                  </TableCell>
                  <TableCell>{order.order_id}</TableCell>
                  <TableCell>{order.reference_id}</TableCell>
                  <TableCell>{formatDate(order.created_at)}</TableCell>
                  <TableCell>
                    <Chip
                      label={order.courier?.toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{order.tracking_number || "—"}</TableCell>
                  <TableCell>
                    {order.label_url ? (
                      <Tooltip title="Ver etiqueta">
                        <IconButton
                          size="small"
                          onClick={() => window.open(order.label_url, "_blank")}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[order.delivery_status] || order.delivery_status}
                      size="small"
                      color="default"
                    />
                  </TableCell>
                  <TableCell>
                    {order.assigned_to_pickup ? (
                      <Chip
                        label={
                          order.assigned_pickup_id
                            ? `Retiro ${order.assigned_pickup_id}`
                            : "Asignado"
                        }
                        size="small"
                        color="success"
                      />
                    ) : (
                      <Chip label="Pendiente" size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog: seleccionar solicitud de retiro */}
        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Asignar pedidos a solicitud de retiro</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selecciona la solicitud de retiro a la que quieres asignar los{" "}
              <strong>{selectedOrders.length}</strong> pedidos seleccionados.
            </Typography>
            {pickupsForAssignDialog.length === 0 && (
              <Alert severity="warning">
                No hay solicitudes de retiro disponibles. Crea una primero.
              </Alert>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell><strong>ID Recogida</strong></TableCell>
                  <TableCell><strong>Courier</strong></TableCell>
                  <TableCell><strong>Fecha programada</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pickupsForAssignDialog.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    selected={selectedPickupForAssign === p.id}
                    onClick={() => setSelectedPickupForAssign(p.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPickupForAssign === p.id}
                        onChange={() => setSelectedPickupForAssign(p.id)}
                      />
                    </TableCell>
                    <TableCell>{p.external_pickup_id}</TableCell>
                    <TableCell>{p.courier?.toUpperCase()}</TableCell>
                    <TableCell>
                      {formatDateOnly(p.scheduled_date)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>Cancelar</Button>
            <Button
              variant="contained"
              disabled={!selectedPickupForAssign || loading}
              onClick={handleAssignConfirm}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? "Asignando..." : "Confirmar asignación"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(success)}
          autoHideDuration={5000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default AssignToManifest;
